import {useEffect, useSyncExternalStore} from 'react';

export type AgentActionArgs = Record<string, unknown>;
export type AgentActionHandler = (args: AgentActionArgs) => void;

const handlers = new Map<string, AgentActionHandler>();
const listeners = new Set<() => void>();

function emitChange(): void {
  for (const listener of listeners) {
    listener();
  }
}

export function registerAgentActionHandler(
  name: string,
  handler: AgentActionHandler
): () => void {
  handlers.set(name, handler);
  emitChange();
  return () => {
    if (handlers.get(name) === handler) {
      // guard a re-register racing cleanup
      handlers.delete(name);
      emitChange();
    }
  };
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

export function useAgentActionHandler(
  name: string
): AgentActionHandler | undefined {
  return useSyncExternalStore(
    subscribe,
    () => handlers.get(name),
    () => handlers.get(name)
  );
}

/** Memoize `handler` (useCallback) so it doesn't re-register every render. */
export function useRegisterAgentActionHandler(
  name: string,
  handler: AgentActionHandler
): void {
  useEffect(() => registerAgentActionHandler(name, handler), [name, handler]);
}
